import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useParams, Link } from 'react-router-dom';
import { HiCheckCircle, HiArrowLeft, HiOutlineChatBubbleLeftRight, HiXMark, HiArrowUpRight } from 'react-icons/hi2';
import Swal from 'sweetalert2';
import { getProducts } from '../data/productsData';
import { saveEnquiry } from '../data/enquiriesData';

export default function GeneratorProductDetail() {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [formData, setFormData] = useState({ name: '', phone: '', email: '', city: '', message: '' });
  const [isSubmitted, setIsSubmitted] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    const loadProduct = async () => {
      const prods = await getProducts();
      const matched = prods.find(p => p.id === id);
      setProduct(matched || null);
    };
    loadProduct();
  }, [id]);

  useEffect(() => {
    if (isLightboxOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isLightboxOpen]);

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();

    const phoneDigits = formData.phone.trim();
    if (!/^\d{10}$/.test(phoneDigits)) {
      Swal.fire({
        title: 'Invalid Phone Number',
        text: 'Please enter a valid 10-digit number.',
        icon: 'warning',
        confirmButtonColor: '#0F5A3A'
      });
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email.trim())) {
      Swal.fire({
        title: 'Invalid Email Address',
        text: 'Please enter a valid email address.',
        icon: 'warning',
        confirmButtonColor: '#0F5A3A'
      });
      return;
    }

    setIsSubmitted(true);
    
    const newEnquiry = {
      id: `enq-${Date.now()}`,
      name: formData.name,
      phone: formData.phone,
      email: formData.email,
      productId: product.id,
      productName: product.name,
      city: formData.city,
      message: formData.message || `Interested in ${product.name}`,
      type: 'sales',
      status: 'pending',
      date: new Date().toISOString().replace('T', ' ').slice(0, 16)
    };

    try {
      await saveEnquiry(newEnquiry, true);
      Swal.fire({
        title: 'Enquiry Received!',
        text: `Thank you, ${formData.name}. We have received your query for the "${product.name}". Our engineers will call you shortly.`,
        icon: 'success',
        confirmButtonColor: '#0F5A3A'
      });
      setFormData({ name: '', phone: '', email: '', city: '', message: '' });
    } catch (err) {
      Swal.fire('Error', 'Failed to submit enquiry. Please try again.', 'error');
    } finally {
      setIsSubmitted(false);
    }
  };

  if (!product) {
    return (
      <div className="pt-36 pb-24 max-w-7xl mx-auto px-6 lg:px-10 text-center">
        <h2 className="text-xl font-bold text-heading mb-4">Product Not Found</h2>
        <Link to="/generator-sales" className="btn-primary inline-flex">
          <HiArrowLeft className="w-4 h-4" />
          Back to Sales Catalog
        </Link>
      </div>
    );
  }

  return (
    <div className="page-enter bg-light font-sans pt-24">

      {/* Breadcrumb */}
      <div className="bg-white border-b border-border py-3.5">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-2 text-xs text-body-muted">
            <Link to="/" className="hover:text-primary transition-colors">Home</Link>
            <span className="text-border-dark">/</span>
            <Link to="/generator-sales" className="hover:text-primary transition-colors">Generator Sales</Link>
            <span className="text-border-dark">/</span>
            <span className="font-semibold text-primary truncate max-w-[150px] sm:max-w-[280px]">{product.name}</span>
          </div>
          <Link to="/generator-sales" className="flex items-center gap-1.5 text-xs font-bold text-primary hover:text-accent transition-colors self-start sm:self-auto">
            <HiArrowLeft className="w-3.5 h-3.5" />
            Back to Catalog
          </Link>
        </div>
      </div>

      {/* Product Detail Grid */}
      <section className="py-16 max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">

          {/* Left: Image + Details + Specs */}
          <div className="lg:col-span-7 space-y-6">

            {/* Image */}
            <div
              onClick={() => setIsLightboxOpen(true)}
              className="bg-white border border-border rounded-2xl overflow-hidden shadow-card cursor-zoom-in relative group aspect-video"
            >
              <img src={product.image} alt={product.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              <div className="absolute inset-0 bg-heading/0 group-hover:bg-heading/10 transition-colors flex items-center justify-center">
                <span className="opacity-0 group-hover:opacity-100 bg-heading/75 text-white text-xs font-bold px-4 py-2 rounded-xl backdrop-blur-sm transition-opacity duration-300">
                  Click to View Full Size
                </span>
              </div>
            </div>

            {/* Product Description */}
            <div className="bg-white border border-border rounded-2xl p-8 shadow-card">
              <span className="badge-gold text-[10px] mb-4 block w-fit">{product.category}</span>
              <h2 className="text-2xl font-display font-extrabold text-heading mb-4">{product.name}</h2>
              <p className="text-sm text-body leading-relaxed mb-6">{product.desc}</p>

              <div className="border-t border-border pt-6">
                <h3 className="text-xs font-bold text-heading uppercase tracking-wider mb-5">Key Equipment Features</h3>
                <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {product.features.map((feat, idx) => (
                    <li key={idx} className="bg-light-section border border-border rounded-xl p-4 flex items-start gap-3 group hover:border-primary/20 transition-colors">
                      <HiCheckCircle className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                      <span className="text-xs text-body leading-relaxed">{feat}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Tech Specs Table */}
            <div className="bg-white border border-border rounded-2xl overflow-hidden shadow-card">
              <div className="px-6 py-4 bg-primary border-b border-primary-dark">
                <h3 className="text-xs font-bold text-white uppercase tracking-wider">Technical Specifications</h3>
              </div>
              <table className="w-full">
                <tbody className="divide-y divide-border text-sm">
                  {Object.entries(product.specs).map(([label, val], idx) => (
                    <tr key={idx} className="hover:bg-light-section transition-colors">
                      <td className="p-4 font-bold text-heading bg-light-section/50 border-r border-border w-2/5 text-xs">{label}</td>
                      <td className="p-4 font-bold text-accent text-sm">{val}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Right: Sticky Enquiry Form */}
          <div className="lg:col-span-5 sticky top-24">
            <div className="bg-charcoal text-white border border-white/5 rounded-3xl p-8 shadow-premium relative overflow-hidden">
              <div className="absolute -top-10 -right-10 w-36 h-36 bg-accent/8 rounded-full blur-2xl" />
              <div className="relative z-10">
                <div className="flex items-center gap-3 mb-2">
                  <HiOutlineChatBubbleLeftRight className="w-5 h-5 text-accent" />
                  <span className="text-xs font-bold text-accent uppercase tracking-widest">Product Enquiry</span>
                </div>
                <h3 className="text-lg font-display font-extrabold text-white mb-2">Get a Quote</h3>
                <p className="text-xs text-white/60 mb-7">
                  Complete your contact details to receive a custom quote for the <strong className="text-white/80">{product.name}</strong>.
                </p>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block text-[10px] text-white/50 mb-1.5 font-bold uppercase tracking-widest">Full Name</label>
                    <input type="text" name="name" value={formData.name} onChange={handleChange} required placeholder="Enter your name" className="input-dark" />
                  </div>
                  <div>
                    <label className="block text-[10px] text-white/50 mb-1.5 font-bold uppercase tracking-widest">Contact Number</label>
                    <input type="tel" name="phone" value={formData.phone} onChange={handleChange} required placeholder="e.g. +91 98765 43210" className="input-dark" />
                  </div>
                  <div>
                    <label className="block text-[10px] text-white/50 mb-1.5 font-bold uppercase tracking-widest">Email Address</label>
                    <input type="email" name="email" value={formData.email} onChange={handleChange} required placeholder="name@company.com" className="input-dark" />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-[10px] text-white/50 mb-1.5 font-bold uppercase tracking-widest">City</label>
                      <input type="text" name="city" value={formData.city} onChange={handleChange} required placeholder="e.g. Aurangabad" className="input-dark" />
                    </div>
                    <div>
                      <label className="block text-[10px] text-white/50 mb-1.5 font-bold uppercase tracking-widest">Capacity</label>
                      <input type="text" disabled value={product.specs['Power Rating']} className="input-premium opacity-60 cursor-not-allowed" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-[10px] text-white/50 mb-1.5 font-bold uppercase tracking-widest">Message Details</label>
                    <textarea name="message" value={formData.message} onChange={handleChange} rows={4} required placeholder="Ask about pricing, warranty, shipping timeline..." className="input-dark resize-none" />
                  </div>
                  <button type="submit" disabled={isSubmitted} className="btn-green rounded-xl w-full justify-center py-3.5 text-sm disabled:opacity-60">
                    Request a Quote
                    <HiArrowUpRight className="w-4 h-4" />
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Lightbox */}
      {isLightboxOpen && createPortal(
        <div
          className="fixed inset-0 z-[100] flex flex-col items-center justify-center bg-heading/95 backdrop-blur-md p-4 overflow-hidden"
          onClick={() => setIsLightboxOpen(false)}
        >
          <button
            onClick={() => setIsLightboxOpen(false)}
            className="absolute top-6 right-6 p-2 rounded-full bg-white/10 text-white hover:bg-accent transition-all shadow-lg"
          >
            <HiXMark className="w-6 h-6" />
          </button>
          <div className="max-w-4xl max-h-[80vh] flex flex-col items-center select-none" onClick={(e) => e.stopPropagation()}>
            <img src={product.image} alt={product.name} className="max-w-full max-h-[70vh] object-contain rounded-2xl shadow-2xl border border-white/10" />
            <h3 className="text-white text-lg font-display font-bold text-center mt-6">{product.name}</h3>
            <span className="text-accent text-xs font-bold uppercase tracking-widest mt-1">Rating: {product.specs['Power Rating']}</span>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
